import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { X, ZoomIn, ZoomOut, RotateCw } from "lucide-react";
import "./ImageZoomModal.scss";

const ImageZoomModal = ({
  show,
  imageUrl,
  onClose,
  images = [],
  currentIndex = 0,
}) => {
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [activeIndex, setActiveIndex] = useState(currentIndex);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Determine if we have multiple images or single image
  const hasMultipleImages = images && images.length > 1;
  const imageList =
    images && images.length > 0 ? images : imageUrl ? [imageUrl] : [];
  const currentImageUrl = imageList[activeIndex] || imageUrl;

  useEffect(() => {
    if (!show) {
      setScale(1);
      setRotation(0);
      setPosition({ x: 0, y: 0 });
      setActiveIndex(currentIndex);
    }
  }, [show, currentIndex]);

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 4));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleReset = () => {
    setScale(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
  };

  const handlePrevImage = () => {
    if (hasMultipleImages && activeIndex > 0) {
      setActiveIndex((prev) => prev - 1);
      handleReset();
    }
  };

  const handleNextImage = () => {
    if (hasMultipleImages && activeIndex < imageList.length - 1) {
      setActiveIndex((prev) => prev + 1);
      handleReset();
    }
  };

  const handleMouseDown = (e) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Keyboard navigation
  useEffect(() => {
    if (!show) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case "Escape":
          onClose();
          break;
        case "+":
        case "=":
          handleZoomIn();
          break;
        case "-":
          handleZoomOut();
          break;
        case "r":
        case "R":
          handleRotate();
          break;
        case "0":
          handleReset();
          break;
        case "ArrowLeft":
          handlePrevImage();
          break;
        case "ArrowRight":
          handleNextImage();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [show, scale, activeIndex, imageList]);

  if (!currentImageUrl) {
    return null;
  }

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="xl"
      className="image-zoom-modal"
      backdrop="static"
      dialogClassName="image-zoom-dialog"
    >
      <Modal.Body className="p-0">
        <div className="zoom-header">
          <div className="zoom-controls">
            <button onClick={handleZoomOut} title="Zoom Out (-)">
              <ZoomOut size={20} />
            </button>
            <span className="zoom-level">{Math.round(scale * 100)}%</span>
            <button onClick={handleZoomIn} title="Zoom In (+)">
              <ZoomIn size={20} />
            </button>
            <button onClick={handleRotate} title="Rotate (R)">
              <RotateCw size={20} />
            </button>
          </div>
          <button
            onClick={onClose}
            className="close-button"
            title="Close (Esc)"
          >
            <X size={24} />
          </button>
        </div>

        <div
          className="image-container"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <img
            src={currentImageUrl}
            alt="Zoomed view"
            style={{
              transform: `translate(${position.x}px, ${position.y}px) scale(${scale}) rotate(${rotation}deg)`,
              cursor:
                scale > 1 ? (isDragging ? "grabbing" : "grab") : "default",
            }}
            draggable={false}
            onError={(e) => {
              console.error("Image failed to load:", currentImageUrl);
              e.target.style.display = "none";
            }}
          />
        </div>

        {hasMultipleImages && (
          <div className="image-navigation">
            <button
              onClick={handlePrevImage}
              disabled={activeIndex === 0}
              className="nav-btn prev-btn"
              title="Previous (←)"
            >
              ‹
            </button>
            <span className="image-counter">
              {activeIndex + 1} / {imageList.length}
            </span>
            <button
              onClick={handleNextImage}
              disabled={activeIndex === imageList.length - 1}
              className="nav-btn next-btn"
              title="Next (→)"
            >
              ›
            </button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ImageZoomModal;
